import React, { useState, useMemo, useEffect } from 'react';
import { 
  FileText, 
  MapPin, 
  User, 
  Plus, 
  Trash2,
  Edit3,
  Save,
  X,
  AlertTriangle,
  Info,
  Bell,
  Users,
  Shield,
  Building2,
  Briefcase,
  Link2,
  ChevronDown,
  ChevronUp,
  Baby,
  Heart,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { useLifely } from '../context/LifelyContext';
import ContactSelector from '../components/ContactSelector';
import { CONTACT_ROLES } from '../store/mockData';
import { toast } from 'sonner';

const WILL_HOLDER_OPTIONS = [
  { value: 'lawyer', label: 'Lawyer / Law Firm' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'personal', label: 'Stored Personally' },
  { value: 'other', label: 'Other' },
];

const POA_TYPES = [
  { value: 'financial', label: 'Financial / Property' },
  { value: 'personal', label: 'Personal / Welfare / Medical' },
  { value: 'enduring', label: 'Enduring / Continuing' },
  { value: 'other', label: 'Other' },
];

const ADVISOR_TYPES = [
  { value: 'accountant', label: 'Accountant' },
  { value: 'financial-advisor', label: 'Financial Advisor' },
  { value: 'tax-advisor', label: 'Tax Advisor' },
  { value: 'insurance-advisor', label: 'Insurance Advisor' },
  { value: 'other', label: 'Other Professional' },
];


const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const FieldRow = ({ label, value, children, isEditing }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {isEditing ? children : <p className="text-sm">{value || '—'}</p>}
  </div>
);

const CollapsibleSection = ({ title, icon: Icon, subtitle, children, defaultOpen = false, isEditing = false, onEdit, onSave, onCancel, showEditControls = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Card className="border border-border">
      <div className="flex items-center justify-between px-6 py-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
        >
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-medium text-foreground">{title}</h3>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </button>
        <div className="flex items-center gap-2">
          {showEditControls && isOpen && (
            !isEditing ? (
              <Button onClick={(e) => { e.stopPropagation(); onEdit?.(); }} variant="ghost" size="sm" className="h-8">
                <Edit3 className="w-3.5 h-3.5 mr-1" />Edit
              </Button>
            ) : (
              <>
                <Button onClick={(e) => { e.stopPropagation(); onCancel?.(); }} variant="ghost" size="sm" className="h-8">
                  <X className="w-3.5 h-3.5 mr-1" />Cancel
                </Button>
                <Button onClick={(e) => { e.stopPropagation(); onSave?.(); }} size="sm" className="h-8">
                  <Save className="w-3.5 h-3.5 mr-1" />Save
                </Button>
              </>
            )
          )}
          <button onClick={() => setIsOpen(!isOpen)} className="p-1 hover:bg-muted rounded">
            {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
        </div>
      </div>
      {isOpen && <CardContent className="pt-0 pb-6">{children}</CardContent>}
    </Card>
  );
};

const WillStatus = () => {
  const { 
    willStatus, 
    updateWillStatus, 
    addOtherDocument, 
    removeOtherDocument,
    addPoaHolder,
    removePoaHolder,
    addOtherAdvisor,
    removeOtherAdvisor,
    getContactById,
    fetchWillStatus,
    fetchChildren,
    fetchContacts,
    isAuthenticated,
    loading,
    children: childrenData,
    contacts,
    user
  } = useLifely();

  // Fetch data when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchWillStatus();
      fetchChildren();
      fetchContacts();
    }
  }, [isAuthenticated, fetchWillStatus, fetchChildren, fetchContacts]);

  // Handle null willStatus gracefully
  const status = willStatus || {};
  
  const [isEditing, setIsEditing] = useState(false);
  
  // Section-specific editing states
  const [editingSection, setEditingSection] = useState(null);
  
  // All state variables
  const [willExists, setWillExists] = useState(status.willExists || 'unsure');
  const [dateCreated, setDateCreated] = useState(status.dateCreated || '');
  const [dateUpdated, setDateUpdated] = useState(status.dateUpdated || '');
  const [heldWith, setHeldWith] = useState(status.heldWith || '');
  const [firmName, setFirmName] = useState(status.firmName || '');
  const [location, setLocation] = useState(status.location || '');
  
  // Primary Contact - can be linked to a contact or manual entry
  const initialContact = status.primaryContact || {};
  const [primaryContactId, setPrimaryContactId] = useState(initialContact.contactId || '');
  const [contactName, setContactName] = useState(initialContact.name || '');
  const [contactRole, setContactRole] = useState(initialContact.role || '');
  const [contactEmail, setContactEmail] = useState(initialContact.email || '');

  // Update state when willStatus changes (after fetch)
  useEffect(() => {
    if (willStatus) {
      setWillExists(willStatus.willExists || 'unsure');
      setDateCreated(willStatus.dateCreated || '');
      setDateUpdated(willStatus.dateUpdated || '');
      setHeldWith(willStatus.heldWith || '');
      setFirmName(willStatus.firmName || '');
      setLocation(willStatus.location || '');
      const contact = willStatus.primaryContact || {};
      setPrimaryContactId(contact.contactId || '');
      setContactName(contact.name || '');
      setContactRole(contact.role || '');
      setContactEmail(contact.email || '');
    }
  }, [willStatus]);
  const [contactPhone, setContactPhone] = useState(initialContact.phone || '');
  
  const [reminderDate, setReminderDate] = useState(willStatus.reminderDate || '');
  const [reminderEmail, setReminderEmail] = useState(willStatus.reminderEmail || '');
  const [useAlternativeEmail, setUseAlternativeEmail] = useState(willStatus.useAlternativeEmail || false);
  const [lastReviewedDate, setLastReviewedDate] = useState(willStatus.lastReviewedDate || '');
  const [noChangesAtReview, setNoChangesAtReview] = useState(willStatus.noChangesAtReview || false);
  
  // Will holder contact
  const initialWillHolder = willStatus.willHolder || {};
  const [willHolderContactId, setWillHolderContactId] = useState(initialWillHolder.contactId || '');
  const [willHolderName, setWillHolderName] = useState(initialWillHolder.name || '');
  const [willHolderEmail, setWillHolderEmail] = useState(initialWillHolder.email || '');
  const [willHolderPhone, setWillHolderPhone] = useState(initialWillHolder.phone || '');
  
  const initialScope = willStatus.scope || {};
  const [scopePersonalAssets, setScopePersonalAssets] = useState(initialScope.personalAssets || false);
  const [scopeProperty, setScopeProperty] = useState(initialScope.property || false);
  const [scopeBusinessInterests, setScopeBusinessInterests] = useState(initialScope.businessInterests || false);
  const [scopeDigitalAssets, setScopeDigitalAssets] = useState(initialScope.digitalAssets || false);
  const [scopeOther, setScopeOther] = useState(initialScope.other || false);
  const [scopeOtherText, setScopeOtherText] = useState(initialScope.otherText || '');
  
  // Executors - can be linked to contacts or manual entry
  const initialExecutor1 = willStatus.executor1 || {};
  const initialExecutor2 = willStatus.executor2 || {};
  const [exec1ContactId, setExec1ContactId] = useState(initialExecutor1.contactId || '');
  const [exec1Name, setExec1Name] = useState(initialExecutor1.name || '');
  const [exec1Relationship, setExec1Relationship] = useState(initialExecutor1.relationship || '');
  const [exec1Email, setExec1Email] = useState(initialExecutor1.email || '');
  const [exec1Phone, setExec1Phone] = useState(initialExecutor1.phone || '');
  const [exec2ContactId, setExec2ContactId] = useState(initialExecutor2.contactId || '');
  const [exec2Name, setExec2Name] = useState(initialExecutor2.name || '');
  const [exec2Relationship, setExec2Relationship] = useState(initialExecutor2.relationship || '');
  const [exec2Email, setExec2Email] = useState(initialExecutor2.email || '');
  const [exec2Phone, setExec2Phone] = useState(initialExecutor2.phone || '');
  const [executorNotes, setExecutorNotes] = useState(willStatus.executorNotes || '');
  
  const [poaExists, setPoaExists] = useState(willStatus.poaExists || 'unsure');
  const [poaType, setPoaType] = useState(willStatus.poaType || '');
  const [poaOtherType, setPoaOtherType] = useState(willStatus.poaOtherType || '');
  const [poaActivation, setPoaActivation] = useState(willStatus.poaActivation || '');
  // POA Law Firm
  const [poaLawFirm, setPoaLawFirm] = useState(willStatus.poaLawFirm || '');
  const [poaLawFirmContactId, setPoaLawFirmContactId] = useState(willStatus.poaLawFirmContactId || '');
  const [poaLawFirmContactName, setPoaLawFirmContactName] = useState(willStatus.poaLawFirmContactName || '');
  // POA Attorney (appointed person)
  const [poaAttorneyContactId, setPoaAttorneyContactId] = useState(willStatus.poaAttorneyContactId || '');
  const [poaAttorneyName, setPoaAttorneyName] = useState(willStatus.poaAttorneyName || '');
  // POA Successor (backup)
  const [poaSuccessorContactId, setPoaSuccessorContactId] = useState(willStatus.poaSuccessorContactId || '');
  const [poaSuccessorName, setPoaSuccessorName] = useState(willStatus.poaSuccessorName || '');
  // Legacy POA holder fields
  const [newPoaContactId, setNewPoaContactId] = useState('');
  const [newPoaName, setNewPoaName] = useState('');
  const [newPoaRelationship, setNewPoaRelationship] = useState('');
  const [newPoaEmail, setNewPoaEmail] = useState('');
  const [newPoaPhone, setNewPoaPhone] = useState('');
  
  const [guardianshipExists, setGuardianshipExists] = useState(willStatus.guardianshipExists || 'not-applicable');
  const [guardianshipNotes, setGuardianshipNotes] = useState(willStatus.guardianshipNotes || '');
  const [trustsExist, setTrustsExist] = useState(willStatus.trustsExist || 'unsure');
  
  // Children & Dependents
  const [children, setChildren] = useState(willStatus.children || []);
  const [editingChildId, setEditingChildId] = useState(null);
  const [newChildContactId, setNewChildContactId] = useState('');
  const [newChildName, setNewChildName] = useState('');
  const [newChildDob, setNewChildDob] = useState('');
  const [newChildNotes, setNewChildNotes] = useState('');
  const [newChildGodparent1Id, setNewChildGodparent1Id] = useState('');
  const [newChildGodparent1Name, setNewChildGodparent1Name] = useState('');
  const [newChildGodparent2Id, setNewChildGodparent2Id] = useState('');
  const [newChildGodparent2Name, setNewChildGodparent2Name] = useState('');
  const [newChildIntendedGuardianId, setNewChildIntendedGuardianId] = useState('');
  const [newChildIntendedGuardianName, setNewChildIntendedGuardianName] = useState('');
  const [newChildSpecialNeeds, setNewChildSpecialNeeds] = useState('');
  const [newChildArrangementStatus, setNewChildArrangementStatus] = useState('none');
  
  // Legal Advisor - can be linked to a contact or manual entry
  const initialLegalAdvisor = willStatus.legalAdvisor || {};
  const [legalContactId, setLegalContactId] = useState(initialLegalAdvisor.contactId || '');
  const [legalName, setLegalName] = useState(initialLegalAdvisor.name || '');
  const [legalRole, setLegalRole] = useState(initialLegalAdvisor.role || '');
  const [legalEmail, setLegalEmail] = useState(initialLegalAdvisor.email || '');
  const [legalPhone, setLegalPhone] = useState(initialLegalAdvisor.phone || '');
  const [legalNotes, setLegalNotes] = useState(initialLegalAdvisor.notes || '');
  const [legalIncludeInEstate, setLegalIncludeInEstate] = useState(initialLegalAdvisor.includeInEstate || false);
  const [newAdvisorType, setNewAdvisorType] = useState('');
  const [newAdvisorContactId, setNewAdvisorContactId] = useState('');
  const [newAdvisorName, setNewAdvisorName] = useState('');
  const [newAdvisorEmail, setNewAdvisorEmail] = useState('');
  const [newAdvisorPhone, setNewAdvisorPhone] = useState('');
  const [newAdvisorNotes, setNewAdvisorNotes] = useState('');
  const [newAdvisorIncludeInEstate, setNewAdvisorIncludeInEstate] = useState(false);
  
  const [lifelyReferenced, setLifelyReferenced] = useState(willStatus.lifelyReferenced || 'unsure');
  const [notes, setNotes] = useState(willStatus.notes || '');
  
  const [newDocType, setNewDocType] = useState('');
  const [newDocHeldWith, setNewDocHeldWith] = useState('');
  const [newDocNotes, setNewDocNotes] = useState('');

  const today = new Date().toISOString().split('T')[0];

  // Helper to populate fields from a contact
  const populateFromContact = (contactId, setters) => {
    const contact = getContactById(contactId);
    if (contact) {
      if (setters.setName) setters.setName(contact.name || '');
      if (setters.setEmail) setters.setEmail(contact.email || '');
      if (setters.setPhone) setters.setPhone(contact.phone || '');
      if (setters.setRole) {
        const roleLabel = CONTACT_ROLES.find(r => r.value === contact.role)?.label || contact.role || '';
        setters.setRole(roleLabel);
      }
      if (setters.setRelationship) {
        const roleLabel = CONTACT_ROLES.find(r => r.value === contact.role)?.label || contact.role || '';
        setters.setRelationship(roleLabel);
      }
    }
  };

  // Handler for primary contact selection
  const handlePrimaryContactSelect = (contactId) => {
    setPrimaryContactId(contactId);
    if (contactId) {
      populateFromContact(contactId, {
        setName: setContactName,
        setRole: setContactRole,
        setEmail: setContactEmail,
        setPhone: setContactPhone,
      });
    } else {
      setContactName(''); setContactRole(''); setContactEmail(''); setContactPhone('');
    }
  };

  // Handler for will holder contact selection
  const handleWillHolderContactSelect = (contactId) => {
    setWillHolderContactId(contactId);
    if (contactId) {
      populateFromContact(contactId, {
        setName: setWillHolderName,
        setEmail: setWillHolderEmail,
        setPhone: setWillHolderPhone,
      });
    } else {
      setWillHolderName(''); setWillHolderEmail(''); setWillHolderPhone('');
    }
  };

  // Handler for executor 1 selection
  const handleExec1ContactSelect = (contactId) => {
    setExec1ContactId(contactId);
    if (contactId) {
      populateFromContact(contactId, {
        setName: setExec1Name,
        setRelationship: setExec1Relationship,
        setEmail: setExec1Email,
        setPhone: setExec1Phone,
      });
    } else {
      setExec1Name(''); setExec1Relationship(''); setExec1Email(''); setExec1Phone('');
    }
  };

  // Handler for executor 2 selection
  const handleExec2ContactSelect = (contactId) => {
    setExec2ContactId(contactId);
    if (contactId) {
      populateFromContact(contactId, {
        setName: setExec2Name,
        setRelationship: setExec2Relationship,
        setEmail: setExec2Email,
        setPhone: setExec2Phone,
      });
    } else {
      setExec2Name(''); setExec2Relationship(''); setExec2Email(''); setExec2Phone('');
    }
  };

  // Handler for legal advisor selection
  const handleLegalContactSelect = (contactId) => {
    setLegalContactId(contactId);
    if (contactId) {
      populateFromContact(contactId, {
        setName: setLegalName,
        setRole: setLegalRole,
        setEmail: setLegalEmail,
        setPhone: setLegalPhone,
      });
    }
  };

  // Handler for new POA holder contact selection
  const handleNewPoaContactSelect = (contactId) => {
    setNewPoaContactId(contactId);
    if (contactId) {
      const contact = getContactById(contactId);
      if (contact) {
        setNewPoaName(contact.name || '');
        setNewPoaEmail(contact.email || '');
        setNewPoaPhone(contact.phone || '');
        const roleLabel = CONTACT_ROLES.find(r => r.value === contact.role)?.label || contact.role || '';
        setNewPoaRelationship(roleLabel);
      }
    }
  };

  // Handler for new advisor contact selection
  const handleNewAdvisorContactSelect = (contactId) => {
    setNewAdvisorContactId(contactId);
    if (contactId) {
      const contact = getContactById(contactId);
      if (contact) {
        setNewAdvisorName(contact.name || '');
        setNewAdvisorEmail(contact.email || '');
        setNewAdvisorPhone(contact.phone || '');
      }
    }
  };

  // Child/Dependent management
  const resetChildForm = () => {
    setNewChildContactId('');
    setNewChildName('');
    setNewChildDob('');
    setNewChildGodparent1Id('');
    setNewChildGodparent1Name('');
    setNewChildGodparent2Id('');
    setNewChildGodparent2Name('');
    setNewChildIntendedGuardianId('');
    setNewChildIntendedGuardianName('');
    setNewChildSpecialNeeds('');
    setNewChildNotes('');
    setNewChildArrangementStatus('none');
    setEditingChildId(null);
  };

  const handleChildContactSelect = (contactId) => {
    setNewChildContactId(contactId);
    if (contactId) {
      const contact = getContactById(contactId);
      if (contact) setNewChildName(contact.name || '');
    } else {
      setNewChildName('');
    }
  };

  const handleGodparent1Select = (contactId) => {
    setNewChildGodparent1Id(contactId);
    if (contactId) {
      const contact = getContactById(contactId);
      if (contact) setNewChildGodparent1Name(contact.name || '');
    } else {
      setNewChildGodparent1Name('');
    }
  };

  const handleGodparent2Select = (contactId) => {
    setNewChildGodparent2Id(contactId);
    if (contactId) {
      const contact = getContactById(contactId);
      if (contact) setNewChildGodparent2Name(contact.name || '');
    } else {
      setNewChildGodparent2Name('');
    }
  };

  const handleIntendedGuardianSelect = (contactId) => {
    setNewChildIntendedGuardianId(contactId);
    if (contactId) {
      const contact = getContactById(contactId);
      if (contact) setNewChildIntendedGuardianName(contact.name || '');
    } else {
      setNewChildIntendedGuardianName('');
    }
  };

  const handleAddChild = () => {
    if (!newChildName.trim()) {
      toast.error('Please select or add a contact for the child');
      return;
    }
    const newChild = {
      id: editingChildId || `child-${Date.now()}`,
      contactId: newChildContactId,
      name: newChildName.trim(),
      dateOfBirth: newChildDob,
      godparent1Id: newChildGodparent1Id,
      godparent1Name: newChildGodparent1Name,
      godparent2Id: newChildGodparent2Id,
      godparent2Name: newChildGodparent2Name,
      intendedGuardianId: newChildIntendedGuardianId,
      intendedGuardianName: newChildIntendedGuardianName,
      arrangementStatus: newChildArrangementStatus,
      specialNeeds: newChildSpecialNeeds,
      notes: newChildNotes,
    };
    
    if (editingChildId) {
      setChildren(children.map(c => c.id === editingChildId ? newChild : c));
      toast.success(`Updated ${newChild.name}`);
    } else {
      setChildren([...children, newChild]);
      toast.success(`Added ${newChild.name}`);
    }
    resetChildForm();
  };

  const handleEditChild = (child) => {
    setEditingChildId(child.id);
    setNewChildContactId(child.contactId || '');
    setNewChildName(child.name || '');
    setNewChildDob(child.dateOfBirth || '');
    setNewChildGodparent1Id(child.godparent1Id || '');
    setNewChildGodparent1Name(child.godparent1Name || '');
    setNewChildGodparent2Id(child.godparent2Id || '');
    setNewChildGodparent2Name(child.godparent2Name || '');
    setNewChildIntendedGuardianId(child.intendedGuardianId || '');
    setNewChildIntendedGuardianName(child.intendedGuardianName || '');
    setNewChildArrangementStatus(child.arrangementStatus || 'none');
    setNewChildSpecialNeeds(child.specialNeeds || '');
    setNewChildNotes(child.notes || '');
  };

  const handleRemoveChild = (childId) => {
    setChildren(children.filter(c => c.id !== childId));
    toast.success('Removed child');
  };

  // Calculate child age
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const dateWarnings = useMemo(() => {
    const warnings = [];
    if (dateCreated && dateCreated > today) warnings.push('Date created cannot be in the future');
    if (dateUpdated && dateCreated && dateUpdated < dateCreated) warnings.push('Last updated cannot be before creation date');
    if (dateUpdated && dateUpdated > today) warnings.push('Last updated cannot be in the future');
    return warnings;
  }, [dateCreated, dateUpdated, today]);

  const handleStartEdit = () => {
    setWillExists(willStatus.willExists);
    setDateCreated(willStatus.dateCreated);
    setDateUpdated(willStatus.dateUpdated);
    setHeldWith(willStatus.heldWith);
    setFirmName(willStatus.firmName);
    setLocation(willStatus.location);
    const contact = willStatus.primaryContact || {};
    setPrimaryContactId(contact.contactId || '');
    setContactName(contact.name || '');
    setContactRole(contact.role || '');
    setContactEmail(contact.email || '');
    setContactPhone(contact.phone || '');
    setReminderDate(willStatus.reminderDate || '');
    setLastReviewedDate(willStatus.lastReviewedDate || '');
    setNoChangesAtReview(willStatus.noChangesAtReview || false);
    const holder = willStatus.willHolder || {};
    setWillHolderContactId(holder.contactId || '');
    setWillHolderName(holder.name || '');
    setWillHolderEmail(holder.email || '');
    setWillHolderPhone(holder.phone || '');
    const scope = willStatus.scope || {};
    setScopePersonalAssets(scope.personalAssets || false);
    setScopeProperty(scope.property || false);
    setScopeBusinessInterests(scope.businessInterests || false);
    setScopeDigitalAssets(scope.digitalAssets || false);
    setScopeOther(scope.other || false);
    setScopeOtherText(scope.otherText || '');
    const exec1 = willStatus.executor1 || {};
    const exec2 = willStatus.executor2 || {};
    setExec1ContactId(exec1.contactId || '');
    setExec1Name(exec1.name || '');
    setExec1Relationship(exec1.relationship || '');
    setExec1Email(exec1.email || '');
    setExec1Phone(exec1.phone || '');
    setExec2ContactId(exec2.contactId || '');
    setExec2Name(exec2.name || '');
    setExec2Relationship(exec2.relationship || '');
    setExec2Email(exec2.email || '');
    setExec2Phone(exec2.phone || '');
    setExecutorNotes(willStatus.executorNotes || '');
    setPoaExists(willStatus.poaExists || 'unsure');
    setPoaType(willStatus.poaType || '');
    setPoaOtherType(willStatus.poaOtherType || '');
    setPoaActivation(willStatus.poaActivation || '');
    setGuardianshipExists(willStatus.guardianshipExists || 'not-applicable');
    setGuardianshipNotes(willStatus.guardianshipNotes || '');
    setTrustsExist(willStatus.trustsExist || 'unsure');
    const legal = willStatus.legalAdvisor || {};
    setLegalContactId(legal.contactId || '');
    setLegalName(legal.name || '');
    setLegalRole(legal.role || '');
    setLegalEmail(legal.email || '');
    setLegalPhone(legal.phone || '');
    setLifelyReferenced(willStatus.lifelyReferenced || 'unsure');
    setNotes(willStatus.notes || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    if (dateWarnings.length > 0) {
      toast.error(dateWarnings[0]);
      return;
    }
    updateWillStatus({
      willExists, dateCreated, dateUpdated, heldWith, firmName, location,
      willHolder: { contactId: willHolderContactId, name: willHolderName, email: willHolderEmail, phone: willHolderPhone },
      primaryContact: { contactId: primaryContactId, name: contactName, role: contactRole, email: contactEmail, phone: contactPhone },
      reminderDate, lastReviewedDate, noChangesAtReview,
      scope: { personalAssets: scopePersonalAssets, property: scopeProperty, businessInterests: scopeBusinessInterests, digitalAssets: scopeDigitalAssets, other: scopeOther, otherText: scopeOtherText },
      executor1: { contactId: exec1ContactId, name: exec1Name, relationship: exec1Relationship, email: exec1Email, phone: exec1Phone },
      executor2: { contactId: exec2ContactId, name: exec2Name, relationship: exec2Relationship, email: exec2Email, phone: exec2Phone },
      executorNotes, poaExists, poaType, poaOtherType, poaActivation,
      children,
      legalAdvisor: { contactId: legalContactId, name: legalName, role: legalRole, email: legalEmail, phone: legalPhone },
      lifelyReferenced, notes,
    });
    setIsEditing(false);
    setEditingSection(null);
    toast.success('Changes saved');
  };

  // Section-specific edit handlers
  const handleEditSection = (section) => {
    handleStartEdit();
    setEditingSection(section);
  };

  const handleSaveSection = () => {
    handleSave();
  };

  const handleCancelSection = () => {
    setEditingSection(null);
    setIsEditing(false);
  };

  // Check if a specific section is being edited
  const isSectionEditing = (section) => editingSection === section || (isEditing && editingSection === null);

  const handleAddPoaHolder = () => {
    if (!newPoaName.trim()) { toast.error('Name is required'); return; }
    addPoaHolder({ contactId: newPoaContactId, name: newPoaName, relationship: newPoaRelationship, email: newPoaEmail, phone: newPoaPhone });
    setNewPoaContactId(''); setNewPoaName(''); setNewPoaRelationship(''); setNewPoaEmail(''); setNewPoaPhone('');
    toast.success('POA holder added');
  };

  const handleAddAdvisor = () => {
    if (!newAdvisorName.trim() || !newAdvisorType) { toast.error('Name and type are required'); return; }
    addOtherAdvisor({ contactId: newAdvisorContactId, type: newAdvisorType, name: newAdvisorName, email: newAdvisorEmail, phone: newAdvisorPhone });
    setNewAdvisorContactId(''); setNewAdvisorType(''); setNewAdvisorName(''); setNewAdvisorEmail(''); setNewAdvisorPhone('');
    toast.success('Advisor added');
  };

  const handleAddDocument = () => {
    if (!newDocType.trim()) { toast.error('Document type is required'); return; }
    addOtherDocument({ type: newDocType, heldWith: newDocHeldWith, notes: newDocNotes });
    setNewDocType(''); setNewDocHeldWith(''); setNewDocNotes('');
    toast.success('Document added');
  };

  const documents = willStatus.otherDocuments || [];
  const poaHolders = willStatus.poaHolders || [];
  const otherAdvisors = willStatus.otherAdvisors || [];

  const getHolderLabel = (v) => WILL_HOLDER_OPTIONS.find(o => o.value === v)?.label || '—';
  const getRoleLabel = (v) => CONTACT_ROLES.find(o => o.value === v)?.label || '—';
  const getPoaTypeLabel = (v) => POA_TYPES.find(o => o.value === v)?.label || '—';
  const getAdvisorTypeLabel = (v) => ADVISOR_TYPES.find(o => o.value === v)?.label || '—';

  const scopeItems = [
    scopePersonalAssets && 'Personal assets',
    scopeProperty && 'Property',
    scopeBusinessInterests && 'Business interests',
    scopeDigitalAssets && 'Digital assets',
    scopeOther && (scopeOtherText ? `Other: ${scopeOtherText}` : 'Other')
  ].filter(Boolean);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="will-status-page">
      {/* Header */}
      <section className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground">
          Formal Documents
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Record the existence and location of formal documents. Lifely does not store legal documents or provide legal authority.
        </p>
      </section>

      {/* Edit Controls */}
      <div className="flex justify-end gap-2">
        {!isEditing ? (
          <Button onClick={handleStartEdit} variant="outline" size="sm">
            <Edit3 className="w-4 h-4 mr-2" />Edit
          </Button>
        ) : (
          <>
            <Button onClick={() => setIsEditing(false)} variant="ghost" size="sm">
              <X className="w-4 h-4 mr-2" />Cancel
            </Button>
            <Button onClick={handleSave} size="sm">
              <Save className="w-4 h-4 mr-2" />Save
            </Button>
          </>
        )}
      </div>

      {/* Will - Core Info */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <SectionHeader icon={FileText} title="Will" subtitle="Basic information about your will" />
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Existence */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Does a will exist?</Label>
            {isEditing ? (
              <RadioGroup value={willExists} onValueChange={setWillExists} className="flex gap-6">
                {['yes', 'no', 'unsure'].map(v => (
                  <div key={v} className="flex items-center gap-2">
                    <RadioGroupItem value={v} id={`will-${v}`} />
                    <Label htmlFor={`will-${v}`} className="text-sm font-normal cursor-pointer capitalize">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
            ) : (
              <p className="text-sm font-medium capitalize">{willExists || 'Not specified'}</p>
            )}
          </div>

          {/* Dates & Review - Merged */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <FieldRow label="Date created" value={dateCreated} isEditing={isEditing}>
              <Input type="date" value={dateCreated} onChange={(e) => setDateCreated(e.target.value)} max={today} className="h-9" />
            </FieldRow>
            <FieldRow label="Last updated" value={dateUpdated} isEditing={isEditing}>
              <Input type="date" value={dateUpdated} onChange={(e) => setDateUpdated(e.target.value)} min={dateCreated || undefined} max={today} className="h-9" />
            </FieldRow>
            <FieldRow label="Last reviewed" value={lastReviewedDate} isEditing={isEditing}>
              <Input type="date" value={lastReviewedDate} onChange={(e) => setLastReviewedDate(e.target.value)} max={today} className="h-9" />
            </FieldRow>
            <FieldRow label="Reminder date" value={reminderDate} isEditing={isEditing}>
              <Input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} min={today} className="h-9" />
            </FieldRow>
          </div>

          {/* Email reminder info */}
          {(reminderDate || isEditing) && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Bell className="w-4 h-4 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Lifely can remind you via email to review your will
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Set a reminder date above and we&apos;ll send you an email when it&apos;s time to review your documents.
                  </p>
                </div>
              </div>
              {isEditing && (
                <div className="space-y-3 pl-6">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="use-alt-email" 
                      checked={useAlternativeEmail} 
                      onCheckedChange={setUseAlternativeEmail}
                    />
                    <Label htmlFor="use-alt-email" className="text-sm font-normal cursor-pointer">
                      Send to a different email address
                    </Label>
                  </div>
                  {useAlternativeEmail && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Alternative email for reminders</Label>
                      <Input 
                        type="email" 
                        value={reminderEmail} 
                        onChange={(e) => setReminderEmail(e.target.value)} 
                        placeholder="alternative@email.com"
                        className="h-9 max-w-sm"
                      />
                    </div>
                  )}
                  {!useAlternativeEmail && user?.email && (
                    <p className="text-xs text-muted-foreground">
                      Reminder will be sent to: <span className="font-medium">{user.email}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* No changes at review checkbox */}
          {(lastReviewedDate || isEditing) && (
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Checkbox 
                    id="no-changes" 
                    checked={noChangesAtReview} 
                    onCheckedChange={setNoChangesAtReview} 
                  />
                  <Label htmlFor="no-changes" className="text-sm font-normal cursor-pointer text-muted-foreground">
                    No changes needed at last review
                  </Label>
                </>
              ) : noChangesAtReview ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="w-3 h-3" /> No changes were needed at last review
                </p>
              ) : null}
            </div>
          )}

          {isEditing && dateWarnings.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded">
              <AlertTriangle className="w-3 h-3" />{dateWarnings[0]}
            </div>
          )}

          {/* Held with */}
          <div className="space-y-3 pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Held With</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <FieldRow label="Holder type" value={getHolderLabel(heldWith)} isEditing={isEditing}>
                <Select value={heldWith} onValueChange={setHeldWith}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {WILL_HOLDER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
              <div className="col-span-3 space-y-1.5">
                <Label className="text-xs text-muted-foreground">Firm name (optional)</Label>
                {isEditing ? (
                  <Input value={firmName} onChange={(e) => setFirmName(e.target.value)} placeholder="Firm or organization name" className="h-9" />
                ) : (
                  <p className="text-sm">{firmName || '—'}</p>
                )}
              </div>
            </div>
            
            {/* Contact selector for holder */}
            {isEditing && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Select person from contacts (optional)</Label>
                <ContactSelector
                  value={willHolderContactId}
                  onSelect={handleWillHolderContactSelect}
                  placeholder="Select contact..."
                  categoryFilter="legal"
                  data-testid="will-holder-contact-selector"
                />
              </div>
            )}
            
            {/* Holder contact details - read only when filled from contact */}
            {(willHolderName || willHolderEmail || willHolderPhone) && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Contact name</Label>
                  <p className="text-sm">{willHolderName || '—'}</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm">{willHolderEmail || '—'}</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <p className="text-sm">{willHolderPhone || '—'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Scope */}
          <div className="space-y-2 pt-4 border-t border-border">
            <Label className="text-xs text-muted-foreground">Scope (non-binding)</Label>
            {isEditing ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4">
                  {[
                    { id: 'personal', label: 'Personal assets', state: scopePersonalAssets, setter: setScopePersonalAssets },
                    { id: 'property', label: 'Property', state: scopeProperty, setter: setScopeProperty },
                    { id: 'business', label: 'Business interests', state: scopeBusinessInterests, setter: setScopeBusinessInterests },
                    { id: 'digital', label: 'Digital assets', state: scopeDigitalAssets, setter: setScopeDigitalAssets },
                    { id: 'other', label: 'Other', state: scopeOther, setter: setScopeOther },
                  ].map(item => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Checkbox id={`scope-${item.id}`} checked={item.state} onCheckedChange={item.setter} />
                      <Label htmlFor={`scope-${item.id}`} className="text-sm font-normal cursor-pointer">{item.label}</Label>
                    </div>
                  ))}
                </div>
                {scopeOther && (
                  <Input 
                    value={scopeOtherText} 
                    onChange={(e) => setScopeOtherText(e.target.value)} 
                    placeholder="Please specify other scope..." 
                    className="h-9 max-w-md"
                  />
                )}
              </div>
            ) : (
              <p className="text-sm">{scopeItems.length > 0 ? scopeItems.join(', ') : '—'}</p>
            )}
          </div>

          {/* Reminder info - now shown in the blue box above */}
        </CardContent>
      </Card>

      {/* Executors Section */}
      <CollapsibleSection 
        icon={Users} 
        title="Executors" 
        subtitle="Named in your will to manage your estate"
        defaultOpen={exec1Name || exec2Name}
        showEditControls={true}
        isEditing={isSectionEditing('executors')}
        onEdit={() => handleEditSection('executors')}
        onSave={handleSaveSection}
        onCancel={handleCancelSection}
      >
        <div className="space-y-6">
          {/* Executor 1 */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Executor 1 (Primary)</p>
            {isSectionEditing('executors') && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Select from contacts</Label>
                <ContactSelector
                  value={exec1ContactId}
                  onSelect={handleExec1ContactSelect}
                  placeholder="Select existing contact..."
                  data-testid="executor1-contact-selector"
                />
              </div>
            )}
            {(exec1Name || exec1Email || exec1Phone) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-3 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="text-sm font-medium">{exec1Name || '—'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Relationship</Label>
                  <p className="text-sm">{exec1Relationship || '—'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm">{exec1Email || '—'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <p className="text-sm">{exec1Phone || '—'}</p>
                </div>
              </div>
            )}
            {!exec1Name && !isSectionEditing('executors') && (
              <p className="text-sm text-muted-foreground italic">No executor selected</p>
            )}
          </div>

          {/* Executor 2 */}
          <div className="space-y-3 pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Executor 2 (optional)</p>
            {isSectionEditing('executors') && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Select from contacts</Label>
                <ContactSelector
                  value={exec2ContactId}
                  onSelect={handleExec2ContactSelect}
                  placeholder="Select existing contact..."
                  data-testid="executor2-contact-selector"
                />
              </div>
            )}
            {(exec2Name || exec2Email || exec2Phone) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-3 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="text-sm font-medium">{exec2Name || '—'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Relationship</Label>
                  <p className="text-sm">{exec2Relationship || '—'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm">{exec2Email || '—'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <p className="text-sm">{exec2Phone || '—'}</p>
                </div>
              </div>
            )}
            {!exec2Name && !isSectionEditing('executors') && (
              <p className="text-sm text-muted-foreground italic">No second executor</p>
            )}
          </div>

          {/* Executor Notes */}
          <div className="space-y-1.5 pt-4 border-t border-border">
            <Label className="text-xs text-muted-foreground">How executors should work together</Label>
            {isSectionEditing('executors') ? (
              <Textarea value={executorNotes} onChange={(e) => setExecutorNotes(e.target.value)} placeholder="Notes on collaboration..." className="min-h-[60px] resize-none text-sm" />
            ) : (
              <p className="text-sm">{executorNotes || <span className="text-muted-foreground italic">No notes</span>}</p>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Power of Attorney */}
      <CollapsibleSection 
        icon={Shield} 
        title="Power of Attorney (POA)" 
        defaultOpen={poaExists === 'yes'}
        showEditControls={true}
        isEditing={isSectionEditing('poa')}
        onEdit={() => handleEditSection('poa')}
        onSave={handleSaveSection}
        onCancel={handleCancelSection}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Does a POA exist?</Label>
            {isSectionEditing('poa') ? (
              <RadioGroup value={poaExists} onValueChange={setPoaExists} className="flex gap-6">
                {['yes', 'no', 'unsure'].map(v => (
                  <div key={v} className="flex items-center gap-2">
                    <RadioGroupItem value={v} id={`poa-${v}`} />
                    <Label htmlFor={`poa-${v}`} className="text-sm font-normal cursor-pointer capitalize">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
            ) : (
              <p className="text-sm font-medium capitalize">{poaExists}</p>
            )}
          </div>

          {poaExists === 'yes' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">POA Type</Label>
                  {isSectionEditing('poa') ? (
                    <Select value={poaType} onValueChange={setPoaType}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {POA_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm">{getPoaTypeLabel(poaType)}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">When does it apply?</Label>
                  {isSectionEditing('poa') ? (
                    <Select value={poaActivation} onValueChange={setPoaActivation}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediately">Immediately</SelectItem>
                        <SelectItem value="on-incapacity">On incapacity</SelectItem>
                        <SelectItem value="unsure">Unsure</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm capitalize">{poaActivation ? poaActivation.replace('-', ' ') : '—'}</p>
                  )}
                </div>
              </div>

              {poaType === 'other' && isSectionEditing('poa') && (
                <FieldRow label="Specify type" value={poaOtherType} isEditing={true}>
                  <Input value={poaOtherType} onChange={(e) => setPoaOtherType(e.target.value)} placeholder="Describe type" className="h-9" />
                </FieldRow>
              )}

              {/* Law Firm / Solicitor for EPOA */}
              <div className="space-y-3 pt-4 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Law firm or solicitor who drafted the POA
                </p>
                <p className="text-xs text-muted-foreground -mt-2">
                  Also known as Enduring Power of Attorney (EPOA or POA)
                </p>
                {isSectionEditing('poa') ? (
                  <div className="space-y-3">
                    <Input 
                      value={poaLawFirm} 
                      onChange={(e) => setPoaLawFirm(e.target.value)} 
                      placeholder="Law firm or solicitor name"
                      className="h-9"
                    />
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Legal contact (from your contacts)</Label>
                      <ContactSelector
                        value={poaLawFirmContactId}
                        onSelect={(id) => {
                          setPoaLawFirmContactId(id);
                          const contact = getContactById(id);
                          if (contact) setPoaLawFirmContactName(contact.name);
                        }}
                        placeholder="Select legal contact..."
                        categoryFilter="legal"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {poaLawFirm && <p className="text-sm font-medium">{poaLawFirm}</p>}
                    {poaLawFirmContactName && <p className="text-sm text-muted-foreground">Contact: {poaLawFirmContactName}</p>}
                    {!poaLawFirm && !poaLawFirmContactName && <p className="text-sm text-muted-foreground italic">Not specified</p>}
                  </div>
                )}
              </div>

              {/* POA Roles Structure */}
              <div className="space-y-4 pt-4 border-t border-border">
                {/* Donor (You) - Static */}
                <div className="p-3 rounded-lg bg-muted/30 border border-border">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Donor / Grantor / Principal
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">The person creating the authority (you)</p>
                  <p className="text-sm font-medium">
                    {user?.firstName && user?.lastName 
                      ? `${user.firstName} ${user.lastName}` 
                      : user?.fullName || user?.email || 'You'}
                  </p>
                </div>

                {/* Attorney (Appointed Person) */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Attorney / Agent / Donee
                  </p>
                  <p className="text-xs text-muted-foreground -mt-1">The person appointed to make decisions</p>
                  {isSectionEditing('poa') ? (
                    <ContactSelector
                      value={poaAttorneyContactId}
                      onSelect={(id) => {
                        setPoaAttorneyContactId(id);
                        const contact = getContactById(id);
                        if (contact) setPoaAttorneyName(contact.name);
                      }}
                      placeholder="Select attorney..."
                    />
                  ) : (
                    <p className="text-sm font-medium">{poaAttorneyName || <span className="text-muted-foreground italic">Not specified</span>}</p>
                  )}
                </div>

                {/* Successor (Backup) */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Successor / Backup Attorney
                  </p>
                  <p className="text-xs text-muted-foreground -mt-1">A designated substitute if the primary attorney cannot act</p>
                  {isSectionEditing('poa') ? (
                    <ContactSelector
                      value={poaSuccessorContactId}
                      onSelect={(id) => {
                        setPoaSuccessorContactId(id);
                        const contact = getContactById(id);
                        if (contact) setPoaSuccessorName(contact.name);
                      }}
                      placeholder="Select successor..."
                    />
                  ) : (
                    <p className="text-sm font-medium">{poaSuccessorName || <span className="text-muted-foreground italic">Not specified</span>}</p>
                  )}
                </div>
              </div>

              {/* Legacy POA Holders - kept for backwards compatibility */}
              {poaHolders.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Additional POA Holders</p>
                  {poaHolders.map(h => (
                    <div key={h.id} className="flex items-center justify-between p-3 rounded border border-border bg-muted/30">
                      <div>
                        <p className="text-sm font-medium">{h.name}</p>
                        <p className="text-xs text-muted-foreground">{[h.relationship, h.email, h.phone].filter(Boolean).join(' · ')}</p>
                      </div>
                      {isSectionEditing('poa') && (
                        <Button variant="ghost" size="sm" onClick={() => removePoaHolder(h.id)} className="text-destructive h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* Children & Dependents - Highlighted Section */}
      <CollapsibleSection 
        icon={Baby} 
        title="Children & Dependents" 
        subtitle="Important family information"
        showEditControls={true}
        isEditing={isSectionEditing('children')}
        onEdit={() => handleEditSection('children')}
        onSave={handleSaveSection}
        onCancel={handleCancelSection}
        defaultOpen={children.length > 0}
      >
        <div className="space-y-6">
          {/* Existing Children */}
          {children.length > 0 && (
            <div className="space-y-3">
              {children.map((child, idx) => {
                const age = calculateAge(child.dateOfBirth);
                const isMinor = age !== null && age < 18;
                return (
                  <div 
                    key={child.id} 
                    className={`p-4 rounded-lg border-2 ${isMinor ? 'border-amber-400/50 bg-amber-50/30' : 'border-border bg-muted/20'}`}
                    data-testid={`child-card-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground">{child.name}</p>
                          {age !== null && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isMinor ? 'bg-amber-200 text-amber-800' : 'bg-muted text-muted-foreground'}`}>
                              {age} years old
                            </span>
                          )}
                          {isMinor && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 font-medium">
                              Minor
                            </span>
                          )}
                        </div>
                        
                        {child.dateOfBirth && (
                          <p className="text-xs text-muted-foreground">
                            DOB: {new Date(child.dateOfBirth).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        )}
                        
                        {/* Arrangement Status Badge */}
                        {child.arrangementStatus && child.arrangementStatus !== 'none' && (
                          <div className="mt-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              child.arrangementStatus === 'formal' 
                                ? 'bg-green-100 text-green-700 border border-green-200' 
                                : child.arrangementStatus === 'verbal'
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {child.arrangementStatus === 'formal' && 'Formal arrangement in place'}
                              {child.arrangementStatus === 'verbal' && 'Verbal agreement'}
                            </span>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                          {(child.godparent1Name || child.godparent2Name) && (
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                                <Heart className="w-3 h-3" /> Godparents
                              </p>
                              {child.godparent1Name && <p className="text-sm">{child.godparent1Name}</p>}
                              {child.godparent2Name && <p className="text-sm">{child.godparent2Name}</p>}
                            </div>
                          )}
                          
                          {child.intendedGuardianName && (
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                                <Shield className="w-3 h-3" /> Intended Guardian
                              </p>
                              <p className="text-sm font-medium text-primary">{child.intendedGuardianName}</p>
                            </div>
                          )}
                        </div>
                        
                        {child.specialNeeds && (
                          <div className="mt-2 p-2 bg-blue-50/50 border border-blue-200/50 rounded">
                            <p className="text-xs text-blue-700 font-medium">Special Considerations</p>
                            <p className="text-sm text-blue-900">{child.specialNeeds}</p>
                          </div>
                        )}
                        
                        {child.notes && (
                          <p className="text-xs text-muted-foreground mt-2">{child.notes}</p>
                        )}
                      </div>
                      
                      {isSectionEditing('children') && (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => handleEditChild(child)}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveChild(child.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Add/Edit Child Form */}
          {isSectionEditing('children') && (
            <div className="p-4 border-2 border-dashed border-primary/30 rounded-lg bg-primary/5 space-y-4">
              <p className="text-sm font-medium text-primary">
                {editingChildId ? 'Edit Child or Dependent' : 'Add Child or Dependent'}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Child (select from contacts)</Label>
                  <ContactSelector
                    value={newChildContactId}
                    onSelect={handleChildContactSelect}
                    placeholder="Select child from contacts..."
                    categoryFilter="family"
                    data-testid="child-contact-selector"
                  />
                  {newChildName && (
                    <p className="text-sm font-medium text-foreground mt-1">{newChildName}</p>
                  )}
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Date of Birth</Label>
                  <Input 
                    type="date" 
                    value={newChildDob} 
                    onChange={(e) => setNewChildDob(e.target.value)}
                    className="text-sm"
                    data-testid="new-child-dob"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Godparent 1</Label>
                  <ContactSelector
                    value={newChildGodparent1Id}
                    onSelect={handleGodparent1Select}
                    placeholder="Select godparent..."
                    data-testid="godparent1-selector"
                  />
                  {newChildGodparent1Name && (
                    <p className="text-sm text-muted-foreground">{newChildGodparent1Name}</p>
                  )}
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Godparent 2</Label>
                  <ContactSelector
                    value={newChildGodparent2Id}
                    onSelect={handleGodparent2Select}
                    placeholder="Select godparent..."
                    data-testid="godparent2-selector"
                  />
                  {newChildGodparent2Name && (
                    <p className="text-sm text-muted-foreground">{newChildGodparent2Name}</p>
                  )}
                </div>
              </div>
              
              {/* Arrangement Status */}
              <div className="space-y-2 p-3 bg-muted/30 rounded-lg border border-border">
                <Label className="text-xs text-muted-foreground">Guardianship / Care Arrangement Status</Label>
                <RadioGroup value={newChildArrangementStatus} onValueChange={setNewChildArrangementStatus} className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="formal" id="arr-formal" />
                    <Label htmlFor="arr-formal" className="text-sm font-normal cursor-pointer">Formal arrangement in place</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="verbal" id="arr-verbal" />
                    <Label htmlFor="arr-verbal" className="text-sm font-normal cursor-pointer">Verbal agreement with godparents/family</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="none" id="arr-none" />
                    <Label htmlFor="arr-none" className="text-sm font-normal cursor-pointer">No arrangement yet</Label>
                  </div>
                </RadioGroup>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Intended Guardian (if no formal arrangement)
                </Label>
                <ContactSelector
                  value={newChildIntendedGuardianId}
                  onSelect={handleIntendedGuardianSelect}
                  placeholder="Select intended guardian..."
                  data-testid="guardian-selector"
                />
                {newChildIntendedGuardianName && (
                  <p className="text-sm font-medium text-primary">{newChildIntendedGuardianName}</p>
                )}
                <p className="text-xs text-muted-foreground">This is your intent — it&apos;s not legally binding but helps communicate your wishes</p>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Special Considerations</Label>
                <Textarea 
                  value={newChildSpecialNeeds} 
                  onChange={(e) => setNewChildSpecialNeeds(e.target.value)} 
                  placeholder="Medical needs, allergies, educational considerations, etc."
                  className="min-h-[60px] resize-none text-sm"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Additional Notes</Label>
                <Textarea 
                  value={newChildNotes} 
                  onChange={(e) => setNewChildNotes(e.target.value)} 
                  placeholder="Any other important information..."
                  className="min-h-[50px] resize-none text-sm"
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleAddChild} size="sm">
                  <Plus className="w-4 h-4 mr-1" /> {editingChildId ? 'Update' : 'Add'} Child
                </Button>
                {editingChildId && (
                  <Button onClick={resetChildForm} size="sm" variant="ghost">
                    Cancel Edit
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* Empty State */}
          {children.length === 0 && !isSectionEditing('children') && (
            <div className="text-center py-6">
              <Baby className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No children or dependents added</p>
              <p className="text-xs text-muted-foreground mt-1">Click Edit to add information about your children</p>
            </div>
          )}
          
          {/* Info Note */}
          <div className="flex items-start gap-2 p-3 bg-amber-50/50 border border-amber-200/50 rounded-lg">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              <span className="font-medium">Minor children are highlighted</span> to indicate they may need guardianship arrangements. 
              The &quot;Intended Guardian&quot; field captures your wishes even if you haven&apos;t completed formal legal arrangements.
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* Professional Advisors */}
      <CollapsibleSection 
        icon={Briefcase} 
        title="Professional Advisors"
        showEditControls={true}
        isEditing={isSectionEditing('advisors')}
        onEdit={() => handleEditSection('advisors')}
        onSave={handleSaveSection}
        onCancel={handleCancelSection}
      >
        <div className="space-y-6">
          {/* Legal Advisor */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Legal Advisor</p>
            {isSectionEditing('advisors') && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Select from contacts</Label>
                <ContactSelector
                  value={legalContactId}
                  onSelect={handleLegalContactSelect}
                  placeholder="Select existing contact..."
                  categoryFilter="legal"
                  data-testid="legal-advisor-contact-selector"
                />
              </div>
            )}
            {(legalName || legalEmail || legalPhone) && (
              <div className="p-3 bg-muted/30 rounded-lg space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Name / Firm</Label>
                    <p className="text-sm font-medium">{legalName || '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Role</Label>
                    <p className="text-sm">{legalRole || '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="text-sm">{legalEmail || '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Phone</Label>
                    <p className="text-sm">{legalPhone || '—'}</p>
                  </div>
                </div>
                {/* Notes */}
                {isSectionEditing('advisors') ? (
                  <div className="space-y-1.5 pt-2 border-t border-border">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <Textarea 
                      value={legalNotes} 
                      onChange={(e) => setLegalNotes(e.target.value)}
                      placeholder="Any notes about this advisor..."
                      className="min-h-[60px] resize-none text-sm"
                    />
                  </div>
                ) : legalNotes && (
                  <div className="pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{legalNotes}</p>
                  </div>
                )}
                {/* Include in estate matters checkbox */}
                <div className="flex items-center gap-2 pt-2 border-t border-border">
                  {isSectionEditing('advisors') ? (
                    <>
                      <Checkbox 
                        id="legal-include-estate" 
                        checked={legalIncludeInEstate} 
                        onCheckedChange={setLegalIncludeInEstate}
                      />
                      <Label htmlFor="legal-include-estate" className="text-sm font-normal cursor-pointer">
                        Should be advised or included in estate matters
                      </Label>
                    </>
                  ) : legalIncludeInEstate && (
                    <p className="text-xs text-primary flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Included in estate matters
                    </p>
                  )}
                </div>
              </div>
            )}
            {!legalName && !isSectionEditing('advisors') && (
              <p className="text-sm text-muted-foreground italic">No legal advisor selected</p>
            )}
          </div>

          {/* Other Advisors */}
          <div className="space-y-3 pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Other Advisors</p>
            {otherAdvisors.map(a => (
              <div key={a.id} className="p-3 rounded border border-border bg-muted/30 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{a.name}</p>
                    <p className="text-xs text-muted-foreground">{[getAdvisorTypeLabel(a.type), a.email, a.phone].filter(Boolean).join(' · ')}</p>
                  </div>
                  {isSectionEditing('advisors') && (
                    <Button variant="ghost" size="sm" onClick={() => removeOtherAdvisor(a.id)} className="text-destructive h-8 w-8 p-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {a.notes && <p className="text-xs text-muted-foreground border-t border-border pt-2">{a.notes}</p>}
                {a.includeInEstate && (
                  <p className="text-xs text-primary flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Included in estate matters
                  </p>
                )}
              </div>
            ))}
            {isSectionEditing('advisors') && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Select from contacts</Label>
                  <ContactSelector
                    value={newAdvisorContactId}
                    onSelect={handleNewAdvisorContactSelect}
                    placeholder="Select existing contact..."
                    categoryFilter="financial"
                    data-testid="advisor-contact-selector"
                  />
                </div>
                {newAdvisorName && (
                  <div className="p-3 bg-muted/30 rounded-lg space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{newAdvisorName}</p>
                        <p className="text-xs text-muted-foreground">{[newAdvisorEmail, newAdvisorPhone].filter(Boolean).join(' · ')}</p>
                      </div>
                      <Select value={newAdvisorType} onValueChange={setNewAdvisorType}>
                        <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          {ADVISOR_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Notes</Label>
                      <Textarea 
                        value={newAdvisorNotes} 
                        onChange={(e) => setNewAdvisorNotes(e.target.value)}
                        placeholder="Any notes about this advisor..."
                        className="min-h-[50px] resize-none text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="new-advisor-include-estate" 
                        checked={newAdvisorIncludeInEstate} 
                        onCheckedChange={setNewAdvisorIncludeInEstate}
                      />
                      <Label htmlFor="new-advisor-include-estate" className="text-sm font-normal cursor-pointer">
                        Should be advised or included in estate matters
                      </Label>
                    </div>
                    <Button onClick={handleAddAdvisor} variant="outline" size="sm" className="h-9">
                      <Plus className="w-4 h-4 mr-1" />Add Advisor
                    </Button>
                  </div>
                )}
              </div>
            )}
            {otherAdvisors.length === 0 && !isSectionEditing('advisors') && (
              <p className="text-sm text-muted-foreground italic">No other advisors added</p>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Lifely Reference & Formal Arrangements */}
      <CollapsibleSection 
        icon={Link2} 
        title="Other Information" 
        subtitle="Lifely reference and formal arrangements"
        showEditControls={true}
        isEditing={isSectionEditing('other')}
        onEdit={() => handleEditSection('other')}
        onSave={handleSaveSection}
        onCancel={handleCancelSection}
      >
        <div className="space-y-6">
          {/* Lifely Reference */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Is Lifely referenced in any formal document?</Label>
            {isSectionEditing('other') ? (
              <RadioGroup value={lifelyReferenced} onValueChange={setLifelyReferenced} className="flex gap-6">
                {['yes', 'no', 'unsure'].map(v => (
                  <div key={v} className="flex items-center gap-2">
                    <RadioGroupItem value={v} id={`lifely-${v}`} />
                    <Label htmlFor={`lifely-${v}`} className="text-sm font-normal cursor-pointer capitalize">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
            ) : (
              <p className="text-sm font-medium capitalize">{lifelyReferenced}</p>
            )}
          </div>

          {/* Formal Arrangements */}
          <div className="space-y-3 pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Formal Arrangements</p>
            {documents.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 rounded border border-border bg-muted/30">
                <div>
                  <p className="text-sm font-medium">{d.type}</p>
                  <p className="text-xs text-muted-foreground">{[d.heldWith, d.notes].filter(Boolean).join(' · ')}</p>
                </div>
                {isSectionEditing('other') && (
                  <Button variant="ghost" size="sm" onClick={() => removeOtherDocument(d.id)} className="text-destructive h-8 w-8 p-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {isSectionEditing('other') && (
              <div className="grid grid-cols-3 gap-2">
                <Input value={newDocType} onChange={(e) => setNewDocType(e.target.value)} placeholder="Arrangement type" className="h-9" />
                <Input value={newDocHeldWith} onChange={(e) => setNewDocHeldWith(e.target.value)} placeholder="Held with" className="h-9" />
                <Button onClick={handleAddDocument} variant="outline" size="sm" className="h-9">
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>
            )}
            {documents.length === 0 && !isSectionEditing('other') && (
              <p className="text-sm text-muted-foreground italic">No formal arrangements recorded</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-1.5 pt-4 border-t border-border">
            <Label className="text-xs text-muted-foreground">Additional notes</Label>
            {isSectionEditing('other') ? (
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any important context..." className="min-h-[60px] resize-none text-sm" />
            ) : (
              <p className="text-sm">{notes || <span className="text-muted-foreground italic">No notes</span>}</p>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Notice */}
      <div className="bg-muted/50 rounded-lg p-4 border border-border">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Note:</span> Lifely does not store legal documents or provide legal advice. This section ensures formal documents are visible and locatable when needed.
        </p>
      </div>
    </div>
  );
};

export default WillStatus;
